import { MaterialCommunityIcons } from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import { Keyboard, Platform, StyleSheet, TouchableOpacity, View } from 'react-native';

import { useColor } from '@/hooks/useColor';

import { PredictionType } from '../inputs/TextIputAutocomplete';
import Text from '../Text';

interface PredictionButtonProps {
  item: PredictionType;
  onPredictionTapped: (prediction: PredictionType) => void;
}

const PredictionButton = ({ item, onPredictionTapped }: PredictionButtonProps) => {
  const colors = useColor();
  const hasScore = item.score !== undefined;
  const scorePercent = hasScore ? Math.round(item.score!.combinedNormalized * 100) : 0;

  return (
    <TouchableOpacity
      style={styles.predictionRow}
      onPress={() => {
        onPredictionTapped(item);
        Keyboard.dismiss();
      }}>
      <View style={styles.row}>
        <View style={styles.contentContainer}>
          <Text numberOfLines={1} style={[styles.label, { color: colors.titleText }]} fontWeight="600">
            {item.label}
          </Text>
          {item.extraLabel && (
            <Text ellipsizeMode="middle" numberOfLines={1} style={[styles.extraLabel, { color: colors.subtitleText }]}>
              {item.extraLabel}
            </Text>
          )}
        </View>

        {(hasScore || item.origin) && (
          <View style={styles.badgeWrapper}>
            <LinearGradient
              colors={['#1a1a1a', '#B38B42']}
              start={{ x: 0, y: 0 }}
              end={{ x: 1, y: 1 }}
              style={styles.scoreBadge}>
              <View style={styles.badgeLeft}>
                <MaterialCommunityIcons
                  name={item.origin === 'Semantic Match' || item.origin === 'Hybrid Match' ? 'creation' : 'book-cross'}
                  size={Platform.OS === 'web' ? 12 : 14}
                  color="#F5C542"
                />
                {item.origin && Platform.OS === 'web' && (
                  <Text style={styles.originText} fontWeight="600">
                    {item.origin}
                  </Text>
                )}
              </View>
              {hasScore && (
                <View style={styles.scoreContainer}>
                  <Text style={styles.scoreText} fontWeight="700">
                    {scorePercent}%
                  </Text>
                </View>
              )}
            </LinearGradient>
          </View>
        )}
      </View>
    </TouchableOpacity>
  );
};

const styles = StyleSheet.create({
  predictionRow: {
    paddingVertical: Platform.OS === 'android' ? 10 : 12,
    paddingHorizontal: 4
  },

  row: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: 12
  },

  contentContainer: {
    flexDirection: 'column',
    gap: 2,
    flex: 1
  },

  label: {
    fontSize: 15
  },

  extraLabel: {
    fontSize: 12
  },

  badgeWrapper: {
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 3,
    elevation: 2,
    maxHeight: 38
  },

  scoreBadge: {
    flexDirection: 'row',
    alignItems: 'center',
    borderRadius: 20,
    borderWidth: 1,
    borderColor: 'rgba(179, 139, 66, 0.3)',
    overflow: 'hidden'
  },

  badgeLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingLeft: Platform.OS === 'web' ? 10 : 8,
    paddingRight: Platform.OS === 'web' ? 8 : 6,
    paddingVertical: Platform.OS === 'web' ? 4 : 2,
    gap: 4
  },

  originText: {
    color: '#F5C542',
    fontSize: Platform.OS === 'web' ? 9 : 8,
    textTransform: 'capitalize',
    letterSpacing: 0.5
  },

  scoreContainer: {
    backgroundColor: '#F5C542',
    paddingHorizontal: Platform.OS === 'web' ? 8 : 6,
    paddingVertical: Platform.OS === 'web' ? 4 : 2,
    height: '100%',
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: 'rgba(26, 26, 26, 0.1)'
  },

  scoreText: {
    color: '#4A3700',
    fontSize: Platform.OS === 'web' ? 10 : 9
  }
});

export default PredictionButton;
